import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, GraduationCap, Building2 } from "lucide-react";
import { resolveUrl } from "@/lib/api";

const renderTitle = (title, gold, cyan) => {
  const words = (title || "").split(" ");
  return words.map((w, i) => {
    const clean = w.replace(/[.,!?]/g, "").toLowerCase();
    if (gold && clean === gold.toLowerCase())
      return (
        <span key={i} className="text-gradient-gold">
          {w}{" "}
        </span>
      );
    if (cyan && clean === cyan.toLowerCase())
      return (
        <span key={i} className="text-cyan">
          {w}{" "}
        </span>
      );
    return <span key={i}>{w} </span>;
  });
};

export const Hero = ({ settings = {} }) => {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 600], [0, 120]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.15]);

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16">
      <motion.div style={{ y: yImg, opacity }} className="absolute inset-0 z-0">
        {settings.hero_image && <img src={resolveUrl(settings.hero_image)} alt="" className="w-full h-full object-cover opacity-25" />}
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/85 to-navy" />
      </motion.div>
      <div className="absolute -top-20 -left-24 w-[42rem] h-[42rem] radial-glow z-0" />
      <div className="absolute bottom-0 right-0 w-[34rem] h-[34rem] radial-glow-gold z-0" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 w-full">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex items-center gap-3 mb-8">
          <span className="h-px w-10 bg-gold" />
          <span className="text-xs md:text-sm tracking-[0.3em] uppercase text-gold font-semibold">{settings.hero_badge}</span>
        </motion.div>

        <div className="overflow-hidden">
          <motion.h1
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="font-heading font-extrabold tracking-tighter leading-[0.98] text-5xl sm:text-6xl lg:text-[5.4rem] max-w-5xl"
            data-testid="hero-headline"
          >
            {renderTitle(settings.hero_title, settings.hero_highlight, settings.hero_highlight2)}
          </motion.h1>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-8 max-w-xl text-base md:text-lg text-slate-300 leading-relaxed"
          data-testid="hero-subheadline"
        >
          {settings.hero_subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#services"
            data-testid="hero-cta-student"
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-cyan/60 bg-cyan/10 px-7 py-4 font-body font-bold text-cyan hover:bg-cyan hover:text-navy hover:shadow-[0_0_30px_rgba(0,240,255,0.4)] transition-all duration-300"
          >
            <GraduationCap className="w-5 h-5" /> Program Siswa
          </a>
          <a
            href="#contact"
            data-testid="hero-cta-corporate"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-4 font-body font-bold text-navy hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] hover:-translate-y-0.5 transition-all duration-300"
          >
            <Building2 className="w-5 h-5" /> Corporate Training
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
        <span className="text-[10px] tracking-[0.3em] uppercase text-slate-500">Scroll</span>
        <span className="w-px h-10 bg-gradient-to-b from-gold to-transparent" />
      </motion.div>
    </section>
  );
};
